import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Group,
  Text,
  TextInput,
  Select,
  Button,
  Paper,
  Stack,
  Badge,
  NumberInput,
  ActionIcon,
  Tooltip,
  Alert,
  LoadingOverlay
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { IconPlus, IconFileText, IconCurrencyDollar, IconX, IconSearch, IconDots, IconWand, IconAlertCircle } from '@tabler/icons-react';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData'; // Adjust the import path as needed
import config from '../../../config';

const QuoteSelect = () => {
  const navigate = useNavigate();
  const [searchOn, setSearchOn] = useState('Headers');
  const [startWith, setStartWith] = useState('');
  const [customer, setCustomer] = useState('DELTA MATERIAL SERVI');
  const [lineCategory, setLineCategory] = useState('All');
  const [maxResults, setMaxResults] = useState(50);

  // Fetch quote data from API
  const { data: quoteData, isLoading, error, refetch } = useAuthenticatedData(`${config.apiBaseUrl}/erp/quotes/top-quotes`);

  // Transform API data to match component structure
  const transformedQuoteData = useMemo(() => {
    if (!quoteData || !Array.isArray(quoteData)) return [];
    
    return quoteData.map(quote => ({
      quoteNo: (quote.useR_DOC || quote.USER_DOC || '').trim(),
      accountName: (quote.name || quote.NAME || '').trim(),
      docType: (quote.doC_TYPE || quote.DOC_TYPE || '').trim(),
      accountRatingCode: (quote.accounT_RATE || quote.ACCOUNT_RATE || '').trim(),
      accountRatingName: (quote.accounT_NAME || quote.ACCOUNT_NAME || '').trim(),
      so: (quote.sO_USER_DOC || quote.SO_USER_DOC || '').trim(),
      customer: (quote.name || quote.NAME || '').trim(),
      // Additional fields that might be useful
      custPo: (quote.cusT_PO || quote.CUST_PO || '').trim(),
      recvDate: quote.recV_DATE || quote.RECV_DATE,
      pricedDate: quote.priceD_DATE || quote.PRICED_DATE,
      enterDate: quote.enteR_DATE || quote.ENTER_DATE,
      docDate: quote.doC_DATE || quote.DOC_DATE,
      dueDate: quote.duE_DATE || quote.DUE_DATE,
      printedDate: quote.printeD_DATE || quote.PRINTED_DATE,
      docTotal: quote.doC_TOTAL || quote.DOC_TOTAL || 0,
      cDocTotal: quote.c_DOC_TOTAL || quote.C_DOC_TOTAL || 0,
      custRef1: (quote.cusT_REF1 || quote.CUST_REF1 || '').trim(),
      custRef2: (quote.cusT_REF2 || quote.CUST_REF2 || '').trim(),
      custRef3: (quote.cusT_REF3 || quote.CUST_REF3 || '').trim(),
      custRef4: (quote.cusT_REF4 || quote.CUST_REF4 || '').trim(),
      docStatus: quote.doC_STATUS || quote.DOC_STATUS,
      priority: quote.priority || quote.PRIORITY,
      assignedBuyerName: (quote.assigneD_BUYER_NAME || quote.ASSIGNED_BUYER_NAME || '').trim(),
      contactName: (quote.contacT_NAME || quote.CONTACT_NAME || '').trim(),
      missingSignatures: quote.missinG_SIGNATURES || quote.MISSING_SIGNATURES,
      salesperson: (quote.fulL_NAME || quote.FULL_NAME || '').trim(),
      salesCode: (quote.smaN1_CODE || quote.SMAN1_CODE || '').trim(),
      accountNo: (quote.acctno || quote.ACCTNO || '').trim(),
      docNo: quote.doC_NO || quote.DOC_NO,
      docCategory: (quote.doC_CATEGORY || quote.DOC_CATEGORY || '').trim(),
      currencyBase: (quote.curencY_BASE || quote.CURRENCY_BASE || '').trim()
    }));
  }, [quoteData]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'quoteNo',
        header: 'Quote No',
        size: 100,
        Cell: ({ cell }) => (
          <Text size="sm" c="blue" fw={500}>
            {cell.getValue()}
          </Text>
        ),
      },
      {
        accessorKey: 'accountName',
        header: 'Account Name',
        size: 200,
        Cell: ({ cell }) => (
          <Text size="sm">
            {cell.getValue()}
          </Text>
        ),
      },
      {
        accessorKey: 'docType',
        header: 'Doc Type',
        size: 80,
        Cell: ({ cell }) => (
          <Text size="sm">
            {cell.getValue()}
          </Text>
        ),
      },
      {
        accessorKey: 'accountRatingCode',
        header: 'Rating Code',
        size: 100,
        Cell: ({ cell }) => (
          <Text size="sm">
            {cell.getValue()}
          </Text>
        ),
      },
      {
        accessorKey: 'accountRatingName',
        header: 'Rating Name',
        size: 180,
        Cell: ({ cell }) => (
          <Text size="sm">
            {cell.getValue()}
          </Text>
        ),
      },
      {
        accessorKey: 'salesperson',
        header: 'Salesperson',
        size: 120,
        Cell: ({ cell }) => (
          <Text size="sm">
            {cell.getValue()}
          </Text>
        ),
      },
      {
        accessorKey: 'docTotal',
        header: 'Doc Total',
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <Text size="sm" ta="right">
              {value ? `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00'}
            </Text>
          );
        },
      },
      {
        accessorKey: 'docStatus',
        header: 'Status',
        size: 80,
        Cell: ({ cell }) => {
          const status = cell.getValue();
          const getStatusText = (statusCode) => {
            switch (statusCode) {
              case 1: return 'Open';
              case 2: return 'Priced';
              case 3: return 'Approved';
              case 4: return 'New';
              case 5: return 'Converted';
              default: return 'Unknown';
            }
          };
          const getStatusColor = (statusCode) => {
            switch (statusCode) {
              case 1: return 'blue';
              case 2: return 'yellow';
              case 3: return 'green';
              case 4: return 'gray';
              case 5: return 'violet';
              default: return 'gray';
            }
          };
          
          return (
            <Badge color={getStatusColor(status)} size="sm">
              {getStatusText(status)}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'dueDate',
        header: 'Due Date',
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <Text size="sm">
              {value ? new Date(value).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              }) : ''}
            </Text>
          );
        },
      },
      {
        accessorKey: 'enterDate',
        header: 'Enter Date',
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <Text size="sm">
              {value ? new Date(value).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              }) : ''}
            </Text>
          );
        },
      },
    ],
    [],
  );

  const table = useMantineReactTable({
    columns,
    data: transformedQuoteData,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableRowSelection: true,
    enableMultiRowSelection: false,
    getRowId: (row) => row.quoteNo,
    mantineTableProps: {
      highlightOnHover: true,
      withColumnBorders: true,
      withRowBorders: true,
      withTableBorder: true,
      striped: true,
      style: {
        fontSize: '14px'
      }
    },
    mantineTableHeadCellProps: {
      style: {
        backgroundColor: 'var(--mantine-color-gray-1)',
        fontWeight: 600,
        fontSize: '14px'
      }
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        // Navigate to quote edit with the quote number
        navigate(`/erp/quotes/edit/${row.original.quoteNo}`);
      },
      style: {
        cursor: 'pointer'
      }
    }),
    initialState: {
      density: 'xs',
      pagination: {
        pageSize: 50
      }
    },
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableSorting: true,
    mantineTopToolbarProps: {
      style: { display: 'none' }
    },
    mantineBottomToolbarProps: {
      style: { 
        backgroundColor: 'var(--mantine-color-gray-0)',
        borderTop: '1px solid var(--mantine-color-gray-3)'
      }
    }
  });

  const handleSearch = () => {
    // Implement search logic here - for now just refetch the data
    console.log('Searching with:', { searchOn, startWith, customer, lineCategory, maxResults });
    refetch();
  };

  const handleCreateNew = () => {
    // Navigate to new quote creation
    navigate('/erp/quotes/new');
  };

  const handleCreateNewWizard = () => {
    // Navigate to new quote wizard
    navigate('/erp/quotes/new-wizard');
  };

  // Loading and error states
  if (isLoading) {
    return (
      <Box pos="relative" mih={200}>
        <LoadingOverlay visible={true} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
          Error loading quote data: {error.message}
          <Button variant="light" size="xs" ml="md" onClick={refetch}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Title Bar */}
      <Paper withBorder p="sm" mb="md" style={{ backgroundColor: 'var(--mantine-color-blue-6)' }}>
        <Group spacing="xs" align="center">
          <Box
            w={24}
            h={24}
            style={{
              backgroundColor: 'var(--mantine-color-blue-8)',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <IconFileText size={16} color="white" />
          </Box>
          <Text c="white" fw={600} size="lg">Quotation</Text>
          {transformedQuoteData.length > 0 && (
            <Badge color="white" variant="light" ml="auto">
              {transformedQuoteData.length} quotes loaded
            </Badge>
          )}
        </Group>
      </Paper>

      {/* Search Controls */}
      <Paper withBorder p="md" mb="md">
        <Stack spacing="md">
          {/* First Row - Search On and Start With */}
          <Group spacing="md" align="flex-end">
            <Box>
              <Text size="sm" fw={500} mb="xs">Search On :</Text>
              <Group spacing={0}>
                <Button
                  variant={searchOn === 'Headers' ? 'filled' : 'default'}
                  size="sm"
                  onClick={() => setSearchOn('Headers')}
                  style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                >
                  Headers
                </Button>
                <Button
                  variant={searchOn === 'Lines' ? 'filled' : 'default'}
                  size="sm"
                  onClick={() => setSearchOn('Lines')}
                  style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0 }}
                >
                  Lines
                </Button>
              </Group>
            </Box>

            <Box style={{ flex: 1 }}>
              <Text size="sm" fw={500} mb="xs">Start With :</Text>
              <Group spacing="xs">
                <TextInput
                  value={startWith}
                  onChange={(e) => setStartWith(e.target.value)}
                  variant="filled"
                  size="sm"
                  style={{ flex: 1 }}
                />
                <ActionIcon variant="filled" size="sm">
                  <IconDots size={14} />
                </ActionIcon>
              </Group>
            </Box>

            <Box>
              <Group spacing="md" align="flex-end">
                <Box>
                  <Text size="sm" fw={500} mb="xs">Customer :</Text>
                  <Group spacing="xs">
                    <TextInput
                      value={customer}
                      onChange={(e) => setCustomer(e.target.value)}
                      variant="filled"
                      size="sm"
                      w={200}
                      styles={{ input: { fontWeight: 'bold', color: 'var(--mantine-color-blue-6)' } }}
                    />
                    <Button variant="default" size="sm">
                      Pick
                    </Button>
                  </Group>
                </Box>

                <Box>
                  <Text size="sm" fw={500} mb="xs">Line Category :</Text>
                  <Group spacing="xs">
                    <Select
                      value={lineCategory}
                      onChange={(value) => setLineCategory(value)}
                      data={['All', 'Standard', 'Special', 'Service']}
                      variant="filled"
                      size="sm"
                      w={120}
                      styles={{ input: { fontWeight: 'bold', color: 'var(--mantine-color-blue-6)' } }}
                    />
                    <Button variant="default" size="sm">
                      All
                    </Button>
                  </Group>
                </Box>
              </Group>
            </Box>
          </Group>

          {/* Second Row - Max Results */}
          <Group position="right">
            <Group spacing="xs" align="center">
              <Text size="sm" fw={500}>Max Search Result Lines :</Text>
              <NumberInput
                value={maxResults}
                onChange={(value) => setMaxResults(value)}
                variant="filled"
                size="sm"
                w={80}
                min={1}
                max={1000}
              />
            </Group>
          </Group>
        </Stack>
      </Paper>

      {/* Drag to Group Instructions */}
      <Paper withBorder p="xs" mb="sm" style={{ backgroundColor: 'var(--mantine-color-gray-1)' }}>
        <Text size="sm" c="dimmed" fs="italic">
          Drag a column header here to group by that column
        </Text>
      </Paper>

      {/* Data Table */}
      <Paper withBorder>
        <MantineReactTable table={table} />
      </Paper>

      {/* Action Buttons */}
      <Paper withBorder p="md" mt="md">
        <Group position="apart">
          <Group spacing="sm">
            <Button
              leftSection={<IconPlus size={16} />}
              variant="default"
              onClick={handleCreateNew}
            >
              Add
            </Button>
            <Button
              leftSection={<IconWand size={16} />}
              variant="filled"
              onClick={handleCreateNewWizard}
            >
              New Wizard
            </Button>
            <Button
              leftSection={<IconCurrencyDollar size={16} />}
              variant="default"
            >
              Acct Financials
            </Button>
          </Group>

          <Group spacing="sm">
            <Button
              leftSection={<IconSearch size={16} />}
              onClick={handleSearch}
              loading={isLoading}
            >
              OK
            </Button>
            <Button
              leftSection={<IconX size={16} />}
              variant="default"
              color="red"
            >
              Cancel
            </Button>
          </Group>
        </Group>
      </Paper>
    </Box>
  );
};

export default QuoteSelect;